import { useFormikContext } from 'formik';
import { FC, useRef, useState } from 'react';
import _ from 'lodash';
import { QcsButton } from '../common/basic/QcsButton';
import { useTranslation } from 'react-i18next';
import { HeaderLogo } from '../layout/HeaderStyles';
import { QcsTypography } from '../common/basic/QcsTypography';
import styled, { CSSObject } from '@emotion/styled';
import { BASE_URL_WITH_VERSION } from '../../utils/api';
import { BannerInputUploadModal } from '../qcsCompanySettings/BannerInputUploadModal';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { getMediaToken } from '../../utils/storage';
import { ShowImageButton } from '../invitation/Detail/Tabs/ShowImageButton';
import { IconButton } from '@mui/material';

const ImageWrapper = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    margin: '1rem 0',
  })
);

const ImageContainer = styled.div(
  (): CSSObject => ({
    maxHeight: '150px',
    maxWidth: '100%',
  })
);

interface Props {
  questionIndex: number;
  onRemoveOldFile: (document: string) => void;
  disabled?: boolean;
}

export const ImageInput: FC<Props> = ({ questionIndex, onRemoveOldFile, disabled }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const imageUrl = _.get(values, `questions.${questionIndex}.document.path`);
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const mediaToken = getMediaToken();

  const documentId = _.get(values, `questions.${questionIndex}.document.id`);

  const handleUploadFile = () => {
    fileRef.current?.click();
  };

  const handleFileChange = (files: FileList | null) => {
    if (!fileRef.current || !files) {
      return;
    }

    const file = files[0];
    setSelectedFile(file);

    fileRef.current.value = '';
  };

  const handleFileUploaded = (url: string, id: string, filename?: string, mediaType?: string) => {
    if (!selectedFile) {
      return;
    }

    setFieldValue(`questions.${questionIndex}.document.id`, id);
    setFieldValue(`questions.${questionIndex}.document.path`, url);
    setFieldValue(`questions.${questionIndex}.document.fileName`, filename);
    setFieldValue(`questions.${questionIndex}.document.mediaType`, mediaType);

    setSelectedFile(null);
  };

  const handleFileCancel = () => {
    setSelectedFile(null);
  };

  const handleRemoveImage = () => {
    onRemoveOldFile(documentId);
    setFieldValue(`questions.${questionIndex}.document`, undefined);
  };

  return (
    <>
      <QcsTypography variant="h6">{t('questionnaire.questions.image')}</QcsTypography>
      {!!imageUrl && (
        <ImageWrapper>
          <ImageContainer>
            <ShowImageButton tooltipTitle="" documentUrl={`${BASE_URL_WITH_VERSION}${imageUrl}?token=${mediaToken}`}>
              <HeaderLogo
                src={`${BASE_URL_WITH_VERSION}${imageUrl}?token=${mediaToken}`}
                style={{ maxHeight: '150px', cursor: 'pointer' }}
              />
            </ShowImageButton>
          </ImageContainer>
          <IconButton disabled={disabled} onClick={handleRemoveImage} color="error">
            <DeleteForeverIcon />
          </IconButton>
        </ImageWrapper>
      )}
      <BannerInputUploadModal file={selectedFile} onFileUploaded={handleFileUploaded} onCloseOnError={handleFileCancel} />
      <input type="file" ref={fileRef} onChange={({ target }) => handleFileChange(target.files)} hidden />
      {!imageUrl && (
        <QcsButton disabled={disabled} onClick={handleUploadFile}>
          {t('common.uploadFile')}
        </QcsButton>
      )}
    </>
  );
};
